<script lang="ts">
  import type { TocNode } from '../lib/render/toc'

  type Props = {
    nodes: TocNode[]
    activeId: string | null
    onJump: (id: string) => void
    /**
     * Optional filter driven from outside (header search). When set, the
     * TOC keeps any node whose text matches OR whose descendants contain
     * a match — the ancestor path survives so users can see WHERE in the
     * tree their match lives, not just the leaf.
     *
     * The TOC no longer owns a search input of its own — that affordance
     * lives in the header now alongside the preview highlighting, so a
     * single keystroke updates both navigation and content discovery.
     */
    filter?: string
  }

  const { nodes, activeId, onJump, filter = '' }: Props = $props()

  // Recursive filter — if a node matches, keep its full subtree. If only
  // a descendant matches, keep the ancestor path so context survives.
  function filterTree(list: TocNode[], q: string): TocNode[] {
    if (!q) return list
    const ql = q.toLowerCase()
    const visit = (n: TocNode): TocNode | null => {
      const selfMatch = n.text.toLowerCase().includes(ql)
      const filteredKids = n.children
        .map(visit)
        .filter((c): c is TocNode => c !== null)
      if (selfMatch || filteredKids.length > 0) {
        return { ...n, children: selfMatch ? n.children : filteredKids }
      }
      return null
    }
    return list.map(visit).filter((c): c is TocNode => c !== null)
  }

  let trimmedFilter = $derived(filter.trim())
  let visible = $derived(filterTree(nodes, trimmedFilter))
</script>

<aside class="toc" aria-label="Contents">
  <div class="toc-head">Contents</div>
  {#if nodes.length === 0}
    <p class="toc-empty">Headings appear here.</p>
  {:else if visible.length === 0}
    <p class="toc-empty">No headings match "{trimmedFilter}".</p>
  {:else}
    <ol class="toc-list">
      {#each visible as node (node.id)}
        {@render renderNode(node)}
      {/each}
    </ol>
  {/if}
</aside>

{#snippet renderNode(node: TocNode)}
  <li class="toc-item">
    <button type="button" class:active={activeId === node.id} onclick={() => onJump(node.id)}>
      {node.text || '(untitled)'}
    </button>
    {#if node.children.length > 0}
      <ol class="toc-children">
        {#each node.children as child (child.id)}
          {@render renderNode(child)}
        {/each}
      </ol>
    {/if}
  </li>
{/snippet}

<style>
  /* TOC sits transparent on the page sheet — no panel background, no
     dividing border. Two-card minimalism: only the editor + preview
     panes carry surface chrome; everything else is on the bg. */
  .toc {
    width: 220px;
    min-width: 0;
    flex-shrink: 0;
    background: transparent;
    overflow-y: auto;
    padding: var(--space-3) var(--space-2) var(--space-3) 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .toc-head {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
  }
  .toc-empty {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }
  .toc-list,
  .toc-children {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  /* Tree-line: each nested level gets a vertical guide on the left so the
     parent → child relationship is visible at a glance. The indent comes
     from the nested <ol> structure itself (not heading-level math) — that
     way headings that skip a level (h1 → h3) collapse cleanly into the
     nearest ancestor instead of leaving a visual gap. */
  .toc-children {
    margin-left: 9px;
    padding-left: 11px;
    border-left: 1px solid var(--border-subtle);
  }
  .toc-item button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 6px 8px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: var(--text-sm);
    line-height: 1.4;
    cursor: pointer;
  }
  .toc-item button:hover {
    background: var(--bg-sunken);
    color: var(--text-primary);
  }
  .toc-item button.active {
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 500;
  }
  /* Make the active item's tree-line segment match the accent so the path
     to the current heading is faintly highlighted, not just the leaf. */
  .toc-item:has(> button.active) > .toc-children,
  .toc-item:has(.toc-children button.active) > .toc-children {
    border-left-color: var(--accent-soft);
  }
  @media (max-width: 1024px) {
    .toc {
      display: none;
    }
  }
</style>
